import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, Palette, MapPin, Calendar, Star, ArrowRight, Check } from 'lucide-react';
import ContactSection from '../components/ContactSection';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/waitlist/signup`, { email, name, role });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <img src="/utentik-logo.png" alt="utentik" className="h-5" />
          <div className="flex items-center gap-5">
            <a href="#contacto" className="text-sm font-medium text-gray-500 hover:text-primary transition-colors" data-testid="nav-contacto">
              Contacto
            </a>
            <a href="#waitlist" className="text-sm font-semibold text-primary hover:text-primary-hover transition-colors" data-testid="nav-waitlist">
              Lista de espera
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-14">
        <div className="relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#f0fdfa] via-white to-[#eff6ff]" />
          
          <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-12 md:pb-20">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Text */}
              <div className="order-2 md:order-1">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  Próximamente
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-[1.1] mb-5 tracking-tight">
                  Descubre talleres,{' '}<br className="hidden sm:block" />
                  actividades y experiencias<br />
                  <span className="text-primary">cerca de ti</span>
                </h1>
                <p className="text-base md:text-lg text-gray-400 leading-relaxed mb-8 max-w-lg">
                  Conectamos personas con experiencias auténticas en todo México. Desde talleres de cerámica hasta caminatas en la montaña — encuentra tu próxima aventura.
                </p>
                <a
                  href="#waitlist"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary-hover transition-all hover:shadow-lg hover:shadow-primary/20"
                  data-testid="hero-cta"
                >
                  Únete a la lista de espera
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              {/* Image grid */}
              <div className="order-1 md:order-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-3">
                    <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                      <img
                        src="https://images.unsplash.com/photo-1544928147-79a2dbc1f389?w=600&q=80"
                        alt="Taller creativo"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="rounded-2xl overflow-hidden aspect-square">
                      <img
                        src="https://images.unsplash.com/photo-1683553613848-7bf405f7082e?w=400&q=80"
                        alt="Clase de cocina"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="space-y-3 pt-8">
                    <div className="rounded-2xl overflow-hidden aspect-square">
                      <img
                        src="https://images.unsplash.com/photo-1560831340-b9679dc9e9f0?w=400&q=80"
                        alt="Evento grupal"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                      <img
                        src="https://images.unsplash.com/photo-1683105693841-8c81235be472?w=600&q=80"
                        alt="Experiencia gastronómica"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is utentik */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <p className="text-xs font-medium text-primary tracking-widest uppercase mb-3">¿Qué es utentik?</p>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight mb-5">
              Un espacio para <span className="text-primary">vivir, crear y compartir</span> experiencias que te inspiran
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              utentik es la plataforma donde los creadores publican talleres, actividades y experiencias, y los usuarios las descubren, reservan y disfrutan.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: MapPin,
                title: 'Encuentra actividades cerca',
                desc: 'Busca por ubicación, categoría o interés. Desde yoga al amanecer hasta talleres de cocina tradicional.'
              },
              {
                icon: Calendar,
                title: 'Reserva en segundos',
                desc: 'Elige la fecha que te funcione, reserva tu lugar y recibe tu confirmación al instante.'
              },
              {
                icon: Star,
                title: 'Experiencias auténticas',
                desc: 'Cada actividad es creada por personas reales con pasión. Lee reseñas y elige con confianza.'
              }
            ].map((item, i) => (
              <div key={item.title} className="p-6 md:p-8 rounded-2xl bg-gray-50 hover:bg-primary/5 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Creators */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <p className="text-xs font-medium text-primary tracking-widest uppercase mb-3">Para creadores</p>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight mb-5">
                Comparte tu pasión con el mundo
              </h2>
              <p className="text-gray-400 text-base leading-relaxed mb-8">
                ¿Eres chef, artista, instructor de yoga, o tienes una habilidad única? utentik te da las herramientas para publicar tus actividades, gestionar reservas y hacer crecer tu comunidad.
              </p>
              <div className="space-y-4">
                {[
                  'Publica actividades en minutos',
                  'Gestiona reservas y lista de espera',
                  'Comunícate directamente con tus participantes',
                  'Recibe pagos con tus propios métodos',
                  'Dashboard con analytics de tus actividades'
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <p className="text-sm text-gray-600">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/10">
              <img
                src="https://images.unsplash.com/photo-1522065893269-6fd20f6d7438?w=800&q=80"
                alt="Creador preparando actividad"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 md:py-20 bg-primary/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '8+', label: 'Categorías de actividades' },
              { value: 'Gratis', label: 'Publicar actividades' },
              { value: '24/7', label: 'Reservas en cualquier momento' },
              { value: '5 min', label: 'Para publicar una actividad' }
            ].map(item => (
              <div key={item.label}>
                <p className="text-3xl md:text-4xl font-bold text-primary mb-1">{item.value}</p>
                <p className="text-xs md:text-sm text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist Form */}
      <section id="waitlist" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-xs font-medium text-primary tracking-widest uppercase mb-3">Lanzamiento próximo</p>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight mb-4">
              Sé de los primeros en vivir utentik
            </h2>
            <p className="text-gray-400 mb-10">
              Regístrate en nuestra lista de espera y te notificaremos cuando estemos listos. Los primeros en la lista tendrán acceso exclusivo.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="waitlist-form">
                {/* Role selector — visually distinct cards */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => setRole('user')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      role === 'user'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="role-user"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${role === 'user' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                      <Users className="w-4 h-4" />
                    </div>
                    <p className={`text-sm font-semibold ${role === 'user' ? 'text-gray-800' : 'text-gray-500'}`}>
                      Registrarme como usuario
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Quiero descubrir y reservar actividades</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('creator')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      role === 'creator'
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid="role-creator"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${role === 'creator' ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-400'}`}>
                      <Palette className="w-4 h-4" />
                    </div>
                    <p className={`text-sm font-semibold ${role === 'creator' ? 'text-gray-800' : 'text-gray-500'}`}>
                      Registrarme como creador
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Quiero publicar mis talleres y experiencias</p>
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="flex-1 px-5 py-3.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm"
                    data-testid="waitlist-name"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="flex-1 px-5 py-3.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm"
                    data-testid="waitlist-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-8 py-3.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary-hover transition-all disabled:opacity-50 text-sm"
                  data-testid="waitlist-submit"
                >
                  {loading ? 'Registrando...' : 'Unirme a la lista de espera'}
                </button>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </form>
            ) : (
              <div className="bg-primary/5 rounded-2xl p-8 border border-primary/20" data-testid="waitlist-success">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">¡Estás en la lista!</h3>
                <p className="text-gray-500 text-sm">
                  Te enviaremos un correo a <span className="font-medium">{email}</span> cuando utentik esté listo para ti. Mientras tanto, síguenos en redes sociales para enterarte de las novedades.
                </p>
                <div className="flex gap-4 justify-center mt-6">
                  <a href="https://www.instagram.com/utentikcom/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                  <a href="https://www.tiktok.com/@utentikcom" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  </a>
                  <a href="https://www.facebook.com/utentik" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact / Learn more */}
      <ContactSection />

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 text-sm text-gray-400">
              <span>© {new Date().getFullYear()} utentik</span>
              <span className="text-gray-300">·</span>
              <span>México</span>
              <span className="text-gray-300">·</span>
              <Link to="/legal" className="hover:text-gray-600">Privacidad</Link>
              <span className="text-gray-300">·</span>
              <Link to="/legal" className="hover:text-gray-600">Términos</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/utentikcom/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Instagram">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@utentikcom" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="TikTok">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
              <a href="https://www.facebook.com/utentik" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Facebook">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
